import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import type { User, LoginRequest, LoginResponse, MeResponse } from "@cold-crew/shared";
import { api, tokenStore } from "@/lib/api";

interface AuthState {
  user:            User | null;
  isAuthenticated: boolean;
  isLoading:       boolean;
}

interface AuthActions {
  login:  (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

type AuthContextValue = AuthState & AuthActions;

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user,      setUser]      = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Ref para cancelar la verificación en background si ya se hizo logout o login
  const ignoreBgVerify = useRef(false);

  useEffect(() => {
    const token = tokenStore.get();
    if (!token) {
      setIsLoading(false);
      return;
    }
    // Mostrar app de inmediato
    setIsLoading(false);
    // Verificar en background
    ignoreBgVerify.current = false;
    api
      .get<MeResponse>("/auth/me")
      .then((res) => {
        if (!ignoreBgVerify.current) setUser(res.user);
      })
      .catch(() => {
        if (!ignoreBgVerify.current) {
          tokenStore.clear();
          setUser(null);
        }
      });
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    // Cancelar cualquier verificación en background pendiente
    ignoreBgVerify.current = true;
    setIsLoading(true);
    try {
      const res = await api.post<LoginResponse>("/auth/login", credentials);
      tokenStore.set(res.token);
      setUser(res.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    // Cancelar cualquier verificación en background pendiente
    ignoreBgVerify.current = true;
    tokenStore.clear();
    setUser(null);
    api.post("/auth/logout").catch(() => {});
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, isLoading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
